import { api_configuration } from "../../common";
import api_cs = api_configuration.types.client_state.alan_api;
import api_app = api_configuration.libraries.gui_definition_lib.libraries.model_lib.types.application.alan_api;
import api_crep = api_configuration.libraries.gui_definition_lib.libraries.model_lib.types.client_reply.alan_api;
import { IdPathBuilder } from "../navigation/IdPathBuilder";
import { createIdPathBuilderFromNode, createIdPathFromNode } from "../navigation/createIdPathFromNode";
import NodePathBuilder from "../navigation/NodePathBuilder";
import { CollectionPathBuilder } from "../navigation/createCollectionPathBuilder";
import SubscriptionBuilder from "../state 2 interface/SubscriptionBuilder";
import { DatastoreProxy } from "../interface/DatastoreProxy";
import { isSaving } from "../state/Node";
import { pad } from "../utils";
import { ScalarProvider, createVariable } from "../../../../../stream";
import { Pending, Failed, Result } from "../Status";

type NakedIdPath = (["group", string] | ["state", string, string, api_app.Cstate_group] | ["collection", string])[];
type Scope = { node: api_cs.Cnode; path: NakedIdPath };

function getScope(definition: api_app.Cauto_increment, node: api_cs.Cnode): Scope | undefined {
	const path: NakedIdPath = [];
	let scope: api_app.Cancestor_node_path | null = definition.properties.scope;
	let valid = true;
	while (scope) {
		const node_position = node.input.position();
		switch (node_position.variant.name) {
			case "entity": {
				const entity = node_position.variant.definition;
				switch (entity.variant.name) {
					case "root": {
						valid = false;
						scope = null;
						break;
					}
					case "entry": {
						const entry = entity.variant.definition;
						const attribute = entry.parent.parent.parent;
						path.push(["collection", attribute.key.entry]);
						node = attribute.parent;
						if (scope.properties.has_steps.state.name === "no") {
							scope = null;
						}
						break;
					}
				}
				break;
			}
			case "group": {
				const attribute = node_position.variant.definition.parent.parent;
				path.push(["group", attribute.key.entry]);
				node = attribute.parent;
				break;
			}
			case "state": {
				const state = node_position.variant.definition;
				const attribute = state.parent.parent.parent;
				path.push(["state", attribute.key.entry, state.key.entry, state.parent.inferences.state_group_type()]);
				node = attribute.parent;
				break;
			}
			case "root":
				if (scope.properties.has_steps.state.name === "yes") {
					valid = false;
				}
				scope = null;
				break;
			default:
				unexpectedState(node_position.variant);
		}
		switch (scope?.properties.has_steps.state.name) {
			case undefined:
				break;
			case "no":
				// don't stop yet, we still have to walk to the entity context
				break;
			case "yes":
				scope = scope.properties.has_steps.state.node.properties.tail;
				break;
		}
	}
	return valid
		? {
				node: node,
				path: path.reverse(),
		  }
		: undefined;
}

function getScopeIdentifier(scope: Scope, text_property_name: string): string {
	let identifier = IdPathBuilder.serialize(createIdPathBuilderFromNode(scope.node));
	for (const step of scope.path) {
		switch (step[0]) {
			case "group":
				identifier = identifier + "+'" + step[1] + "'";
				break;
			case "state":
				identifier = identifier + "?'" + step[1] + "'*'" + step[2] + "'";
				break;
			case "collection":
				identifier = identifier + ".'" + step[1] + "'";
				break;
		}
	}
	identifier = identifier + ".'" + text_property_name + "'";
	return identifier;
}

function findRemoteValues(scope: Scope, text_property_name: string): Promise<Set<string> | undefined> {
	let node_path_builder: NodePathBuilder = new NodePathBuilder();
	let collection_path_builder = new CollectionPathBuilder();
	for (const step of scope.path) {
		switch (step[0]) {
			case "group":
				collection_path_builder = collection_path_builder.group(step[1]);
				node_path_builder = node_path_builder.group(step[1]);
				break;
			case "state":
				collection_path_builder = collection_path_builder.state(step[1], step[2]);
				node_path_builder = node_path_builder.state(step[1], step[2]);
				break;
			case "collection":
				collection_path_builder = collection_path_builder.collection(step[1]);
				node_path_builder = new NodePathBuilder();
				break;
		}
	}
	return DatastoreProxy.instance
		.collectionQuery({
			filters: {
				"todo filter": ["no", {}],
				"value filters": {},
			},
			"context node path": createIdPathFromNode(scope.node),
			"collection path": collection_path_builder.build(),
			"select entries": [
				"yes",
				{
					"limit number of entries": Number.MAX_SAFE_INTEGER,
					properties: {
						properties: {
							[text_property_name]: {
								path: node_path_builder.build(),
								type: [
									"text",
									{
										expression: {
											type: [
												"singular",
												{
													expression: {
														type: [
															"dynamic",
															{
																path: new NodePathBuilder().build(),
																text: text_property_name,
															},
														],
													},
												},
											],
										},
									},
								],
							},
						},
					},
				},
			],
			aggregates: {},
		})
		.then((result) =>
			result.properties.context.switch<Promise<Set<string> | undefined>>({
				exists: ($) => {
					const values = new Set<string>();
					$.properties.entries.switch({
						no: undefined,
						yes: ($) => {
							(function init_entries(entries_type: api_crep.Ccollection_query_entries) {
								entries_type.properties.has_steps.switch({
									no: ($) => {
										const property = $.properties.properties.properties.properties.get(text_property_name);
										property?.properties.result.switch({
											"does not exist": undefined,
											"permission denied": undefined,
											exists: ($) => {
												values.add($.properties.type.cast("text").properties.value);
											},
										});
									},
									yes: ($) => {
										$.properties.entries.forEach((entry) => {
											init_entries(entry.properties.tail);
										});
									},
								});
							})($.properties.entries);
						},
					});
					return Promise.resolve(values);
				},
				"does not exist": () => Promise.resolve(undefined),
				"permission denied": () => Promise.resolve(undefined),
			})
		);
}

function findLocalValues(
	scope: Scope,
	text_property_name: string,
	text_property_to_exclude: api_cs.Ctext__type__property | undefined
): Set<string> {
	let nodes = new Set([scope.node]);
	for (const step of scope.path) {
		switch (step[0]) {
			case "group": {
				const group_nodes = new Set<api_cs.Cnode>();
				for (const node of nodes) {
					const attribute = node.properties.attributes.get(step[1]);
					if (attribute) {
						group_nodes.add(attribute.properties.type.cast("property").properties.type.cast("group").properties.node);
					}
				}
				nodes = group_nodes;
				break;
			}
			case "state": {
				const state_nodes = new Set<api_cs.Cnode>();
				for (const node of nodes) {
					const attribute = node.properties.attributes.get(step[1]);
					if (attribute) {
						const state = attribute.properties.type
							.cast("property")
							.properties.type.cast("state group")
							.properties.states.get(step[2]);
						if (state && state.properties.is_selected.state.name === "yes") {
							state_nodes.add(state.properties.node);
						}
					}
				}
				nodes = state_nodes;
				break;
			}
			case "collection": {
				const entry_nodes = new Set<api_cs.Cnode>();
				for (const node of nodes) {
					const attribute = node.properties.attributes.get(step[1]);
					if (attribute) {
						const collection = attribute.properties.type.cast("property").properties.type.cast("collection");
						for (const entry of collection.properties.entries.values()) {
							entry_nodes.add(entry.properties.node);
						}
					}
				}
				nodes = entry_nodes;
				break;
			}
		}
	}
	const values = new Set<string>();
	for (const node of nodes) {
		node.properties.attributes
			.get(text_property_name)
			?.properties.type.cast("property")
			.properties.type.cast("text")
			.properties.is_set.switch({
				yes: (key_set) => {
					if (key_set.parent !== text_property_to_exclude) {
						values.add(key_set.properties.text);
					}
				},
				no: undefined,
			});
	}
	return values;
}

function increment(values: Set<string>): string | undefined {
	let candidate:
		| {
				prefix: string;
				number_part: string;
				number_value: number;
		  }
		| undefined;

	for (const key of values) {
		const match = /^(.*?)([0-9]*)$/.exec(key);
		if (match !== null) {
			const prefix = match[1];
			const number_part = match[2];
			const number_value = number_part.length > 0 ? Number.parseInt(number_part) : 0;
			if (candidate === undefined || candidate.number_value < number_value) {
				candidate = {
					prefix: prefix,
					number_part: number_part,
					number_value: number_value,
				};
			}
		}
	}
	if (candidate === undefined) {
		return undefined;
	} else {
		const number_part = (candidate.number_value + 1).toString();
		const pad_length = candidate.number_part.length;
		return candidate.prefix + pad(number_part, pad_length, "0");
	}
}

const subscriptions: Record<string, ((value: string | undefined) => any)[]> = {};
function subscribeToRemoteValues(
	scope: Scope,
	text_property_name: string,
	destructor: api_cs.IDestructable,
	on_value: (value: string | undefined) => any
) {
	const scope_id = getScopeIdentifier(scope, text_property_name);
	let datastore_subscription: { unsubscribe: () => void } | undefined;

	if (subscriptions[scope_id] === undefined) {
		subscriptions[scope_id] = [];

		const subscription_builder = SubscriptionBuilder.create();
		let sb = subscription_builder;
		for (const step of scope.path) {
			switch (step[0]) {
				case "group":
					sb = sb.group(step[1]);
					break;
				case "state":
					sb = sb.state(step[1], step[2], step[3]);
					break;
				case "collection":
					sb = sb.collection_entries(step[1]);
					break;
			}
		}
		sb.text(text_property_name);
		const conditions_valid = () =>
			subscriptions[scope_id] !== undefined && !isSaving(scope.node) && !destructor.destroyed && !scope.node.destroyed;
		datastore_subscription = DatastoreProxy.instance.subscribe(
			{
				"context node": createIdPathFromNode(scope.node),
				"mutation permissions": "ignore",
				"node subscription": {
					attributes: subscription_builder.properties,
				},
			},
			() => undefined,
			(notification) => {
				if (!conditions_valid()) {
					return;
				}
				switch (notification.properties.context.state.name) {
					case "exists":
						findRemoteValues(scope, text_property_name).then((values) => {
							if (!conditions_valid()) {
								//Timing can cause the subscription to be deleted during this query
								return;
							}
							let v: string | undefined;
							if (values === undefined) {
								v = values;
							} else {
								v = increment(values);
							}
							for (const on_value of subscriptions[scope_id]) {
								on_value(v);
								if (!(v === undefined)) {
									v = increment(new Set([v]));
								}
							}
						});
						break;
					case "permission denied":
						break;
					case "does not exist":
						break;
				}
			}
		);
	}
	subscriptions[scope_id].push(on_value);
	destructor.onDestroy(() => {
		if (subscriptions[scope_id] !== undefined) {
			const idx = subscriptions[scope_id].indexOf(on_value);
			if (idx >= 0) {
				subscriptions[scope_id].splice(idx, 1);
			}
			if (subscriptions[scope_id].length <= 0) {
				datastore_subscription?.unsubscribe();
				delete subscriptions[scope_id];
			}
		}
	});
}

export function property(definition: api_app.Cauto_increment, this_node: api_cs.Cnode): ScalarProvider<Result<string>> {
	const ui_text = definition.parent.inferences.text_target();
	switch (ui_text.variant.name) {
		case "text property": {
			const app_text = ui_text.variant.definition;
			const text_property_name = app_text.parent.parent.key;
			const stream = createVariable<Result<string>>(Pending, true);
			const scope = getScope(definition, this_node);
			if (!scope) {
				return stream;
			}
			const this_text_property = this_node.properties.attributes
				.get(text_property_name)
				?.properties.type.cast("property")
				.properties.type.cast("text");
			const local_values = findLocalValues(scope, text_property_name, this_text_property);
			switch (scope.node.properties.location.state.name) {
				case "remote": {
					findRemoteValues(scope, text_property_name).then((remote_values) => {
						if (!remote_values) {
							stream.set(Failed);
							return;
						}
						setTimeout(() => {
							if (stream.destroyed) return;
							subscribeToRemoteValues(scope, text_property_name, stream, (value) => {
								if (value === undefined) {
									stream.set(Failed);
								} else {
									stream.set(value);
								}
							});
						}, 0);
						const incremented_value = increment(new Set([...local_values, ...remote_values]));
						if (incremented_value === undefined) {
							stream.set(Failed);
						} else {
							stream.set(incremented_value);
						}
					});
					break;
				}
				case "local": {
					const incremented_value = increment(local_values);
					if (incremented_value === undefined) {
						stream.set(Failed);
					} else {
						stream.set(incremented_value);
					}
					break;
				}
				default:
					unexpectedState(scope.node.properties.location.state);
			}
			return stream;
		}
		case "intermediate result":
			throw new Error("unexpected 'intermediate result'");
		default:
			unexpectedState(ui_text.variant);
	}
}
